import { useNavigate } from 'react-router-dom';
import { Icon } from '../../components/Icon.jsx';
import { useT, useLang } from '../../lib/i18n.jsx';
import { get } from '../../lib/api.js';
import { useAsync, initials } from '../../lib/hooks.js';
import { relativeTime } from '../../lib/format.js';
import { errorMessage } from '../../lib/errors.js';
import { formatBytes, useBackupSummaries } from '../../lib/backups.js';

export default function BackupsPage() {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();

  const { data: hostings, loading, error } = useAsync(() => get('/hostings'), []);
  const summaries = useBackupSummaries(hostings);
  const sites = (hostings || []).filter((h) => h.backup_enabled !== false);

  const totalRetention = sites.length > 0
    ? Math.max(...sites.map((h) => h.backup_retention_days || 0))
    : 0;

  const all = Object.values(summaries || {});
  const lastAt = all
    .map((x) => x.lastCompletedAt)
    .filter(Boolean)
    .sort()
    .pop();
  const rates = all.map((x) => x.successRate).filter((r) => r != null);
  const avgRate = rates.length ? rates.reduce((a, r) => a + r, 0) / rates.length : null;
  const totalBytes = all.reduce((a, x) => a + x.totalBytes, 0);
  const pending = summaries == null && !!hostings?.length;

  return (
    <main className="page" data-screen-label="Backups">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('bk.title')}</h1>
          <p className="page-sub">{t('bk.sub')}</p>
        </div>
        <div className="actions">
          {sites.length > 0 && <span className="status-pill"><span className="dot" />{t('backupsPage.automatic')}</span>}
        </div>
      </div>

      <div className="kpi-grid kpi-grid--4">
        <Kpi
          label={t('bk.kpi.last')}
          value={pending ? '…' : lastAt ? relativeTime(lastAt, lang) : '—'}
          sub={lastAt || pending ? t('backupsPage.kpi.lastSub') : t('backupsPage.kpi.lastNone')}
          icon="rotate"
        />
        <Kpi
          label={t('bk.kpi.success')}
          value={pending ? '…' : avgRate == null ? '—' : `${Math.round(avgRate * 100)}%`}
          sub={t('backupsPage.kpi.successSub')}
          icon="check"
        />
        <Kpi
          label={t('bk.kpi.storage')}
          value={pending ? '…' : all.length ? formatBytes(totalBytes, lang) : '—'}
          sub={t('backupsPage.kpi.storageSub')}
          icon="archive"
        />
        <Kpi
          label={t('bk.kpi.retention')}
          value={totalRetention ? t(totalRetention === 1 ? 'backupsPage.days.one' : 'backupsPage.days.other', { n: totalRetention }) : '—'}
          sub={t('backupsPage.kpi.retentionSub')}
          icon="shield"
        />
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="card-h" style={{ paddingTop: 18 }}>
          <h3>{t('bk.list.title')}</h3>
        </div>
        <div className="bk-head">
          <span>{t('bk.col.site')}</span>
          <span>{t('bk.col.last')}</span>
          <span>{t('bk.col.snapshots')}</span>
          <span>{t('bk.col.size')}</span>
          <span>{t('bk.col.next')}</span>
          <span>{t('bk.col.status')}</span>
          <span />
        </div>
        {loading && <div style={{ padding: '14px 22px', color: 'var(--ink-3)' }}>{t('common.loading')}</div>}
        {!loading && error && (
          <div style={{ padding: '24px 22px', textAlign: 'center', color: 'var(--danger)' }} role="alert">
            {errorMessage(error, { lang })}
          </div>
        )}
        {!loading && !error && (hostings || []).length === 0 && (
          <div style={{ padding: '24px 22px', textAlign: 'center', color: 'var(--ink-3)' }}>
            {t('backupsPage.noHostings')}
          </div>
        )}
        {(hostings || []).map((h) => (
          <div
            key={h.id}
            className="bk-row"
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/backups/${h.id}`)}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/backups/${h.id}`); }}
          >
            <div className="bk-row__site">
              <div className="fav">{initials(h.domain.split('.')[0])}</div>
              <div style={{ minWidth: 0 }}>
                <div className="mono" style={{ fontSize: 13, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {h.domain}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 2 }}>
                  {h.package?.name || '—'}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              {summaries == null ? '…' : summaries[h.id]?.lastCompletedAt ? relativeTime(summaries[h.id].lastCompletedAt, lang) : '—'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              {summaries == null ? '…' : summaries[h.id] ? summaries[h.id].count : '—'}
            </div>
            <div className="mono" style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
              {summaries == null ? '…' : summaries[h.id] ? formatBytes(summaries[h.id].totalBytes, lang) : '—'}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
              {t('backupsPage.every', { h: h.backup_interval_hours || 24 })}
            </div>
            <div>
              {!h.backup_enabled ? null : summaries?.[h.id]?.latestStatus === 'failed' ? (
                <span className="status-pill is-danger"><span className="dot" />{t('backupsPage.failed')}</span>
              ) : summaries?.[h.id]?.count ? (
                <span className="status-pill"><span className="dot" />{t('bk.status.healthy')}</span>
              ) : (
                <span className="status-pill is-warn"><span className="dot" />{t('backupsPage.noBackup')}</span>
              )}
              {h.backup_enabled ? null : (
                <span className="status-pill is-warn"><span className="dot" />{t('backupsPage.off')}</span>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <Icon name="arrow-right" size={14} />
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}

function Kpi({ label, value, sub, icon, progress }) {
  return (
    <div className="kpi-card">
      <div className="kpi-card__top">
        <span className="kpi-card__icon"><Icon name={icon} size={16} /></span>
        <span className="kpi-card__label">{label}</span>
      </div>
      <div className="kpi-card__value" style={{ fontSize: 22 }}>{value}</div>
      <div className="kpi-card__sub">{sub}</div>
      {progress !== undefined && (
        <div className="bar kpi-card__bar">
          <i style={{ width: Math.round(progress * 100) + '%' }} />
        </div>
      )}
    </div>
  );
}
